// Whether the person can see the element: a closed daisyUI dropdown keeps its
// menu in the DOM with `visibility: hidden` and `opacity: 0`, so a rendered
// node is not a visible one. checkVisibility answers both in the browser;
// jsdom lacks it, so the fallback walks the ancestors for the same three
// properties on every ancestor (display and opacity hide the subtree without
// being inherited).
//
// checkVisibility gets both spellings of each option: `opacityProperty` /
// `visibilityProperty` are the current names (Chrome 121, Firefox 122,
// Safari 17.4) and `checkOpacity` / `checkVisibilityCSS` the original ones,
// still accepted as aliases. A browser that knows only one spelling ignores
// the other, so passing one alone would let it count a closed copy as visible.
const VISIBILITY_OPTIONS = {
  opacityProperty: true,
  visibilityProperty: true,
  checkOpacity: true,
  checkVisibilityCSS: true,
} as CheckVisibilityOptions;

export function isVisibleTarget(el: Element): boolean {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility(VISIBILITY_OPTIONS);
  }
  for (let node: Element | null = el; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  }
  return true;
}

// GRO-1247: a selector shared by repeated components (every stage column
// renders its own ⋮ menu, each with `stage-menu-configure`) resolves to the
// copy the person can see — the menu that is open — not the first in the
// DOM, which may sit in a closed menu of another column. With no visible copy
// yet (a menu still fading in from opacity 0) it falls back to the first
// match, and the tracking loop moves to the visible one as soon as it shows.
export function queryVisibleTarget<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T | null {
  const matches = Array.from(root.querySelectorAll<T>(selector));
  return matches.find(isVisibleTarget) ?? matches[0] ?? null;
}
